import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../lib/branding.js';
import { api, ApiError } from '../lib/api.js';
import { PasswordInput } from '../components/ui.js';

const MIN_PASSWORD = 8;

/**
 * Akarya Box first run: one form, one button. Name the place, pick a password
 * (and type the code shown on the box when this isn't the box's own screen),
 * and the console signs in. Reloads on success so capabilities and branding
 * are re-read for the new account.
 */
export function SetupScreen(): ReactNode {
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [status, setStatus] = useState<{ open: boolean; codeRequired: boolean }>();
  const [placeName, setPlaceName] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submitted = useRef(false);

  useEffect(() => {
    api.boxSetupStatus().then(
      (s) => {
        if (s.open) setStatus(s);
        else navigate('/login', { replace: true });
      },
      () => navigate('/login', { replace: true }),
    );
  }, [navigate]);

  const codeRequired = status?.codeRequired === true;
  const ready =
    placeName.trim() !== '' && password.length >= MIN_PASSWORD && (!codeRequired || /^\d{6}$/.test(code));

  async function run(): Promise<void> {
    setError(undefined);
    setBusy(true);
    try {
      await api.boxSetup({
        placeName: placeName.trim(),
        password,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(codeRequired ? { code } : {}),
      });
      window.location.assign(import.meta.env.BASE_URL);
    } catch (err) {
      submitted.current = false;
      setError(
        err instanceof ApiError ? (err.fieldMessage() ?? err.message) : 'Could not reach the server.',
      );
      setBusy(false);
    }
  }

  // The last thing typed is the code: submit as soon as it is complete.
  useEffect(() => {
    if (codeRequired && ready && !busy && !submitted.current) {
      submitted.current = true;
      void run();
    }
  }, [code]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!ready) return;
    submitted.current = true;
    void run();
  }

  if (!status) return null;

  return (
    <div className="auth-wrap">
      <form className="card card-pad auth-card stack" onSubmit={submit}>
        <div>
          <h1 style={{ fontSize: 18 }}>{branding.productName}</h1>
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            Set up this box
          </p>
        </div>
        <label className="field">
          Name of your place
          <input
            type="text"
            value={placeName}
            onChange={(e) => setPlaceName(e.target.value)}
            placeholder="Cafe Akarya"
            required
            autoFocus
          />
        </label>
        <label className="field">
          Password
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            minLength={MIN_PASSWORD}
            required
          />
        </label>
        {codeRequired ? (
          <label className="field">
            Setup code shown on the box
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
            />
          </label>
        ) : null}
        <details>
          <summary className="muted small">Add an email (optional)</summary>
          <label className="field">
            Email
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@box.local"
            />
          </label>
        </details>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="btn primary" type="submit" disabled={busy || !ready}>
          {busy ? 'Setting up…' : 'Start'}
        </button>
      </form>
    </div>
  );
}
